import { isFuture, parseISO } from 'date-fns';
import { useTranslation } from 'next-i18next';
import Error from 'next/error';
import Link from 'next/link';
import React, { useState, VFC } from 'react';

import { AniftyTokenDetailData } from '@/apis/aniftyBackend/aniftyTokens/interface';
import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { MintAskStatusType } from '@/apis/aniftyBackend/mintAsks/interface';
import CreationOwnerList from '@/components/CreationOwnerList';
import ImageExhibition from '@/components/ImageExhibition';
import CreationDeleteModal from '@/components/Modals/CreationDeleteModal';
import ShareSNSButton from '@/components/ShareSNSButton/ShareSNSButton';
import Spinner from '@/components/Spinner';
import {
  IS_LISTING_FEATURE_END,
  IS_PURCHASE_FEATURE_END,
} from '@/config/closure';
import routes from '@/config/routes';
import CreationInfoSection from '@/containers/creations/CreationDetail/CreationInfoSection';
import V1CreationSale from '@/containers/creations/CreationDetail/V1CreationSale';
import V2CreationSale from '@/containers/creations/CreationDetail/V2CreationSale';
import V2CreationSaleIpfs from '@/containers/creations/CreationDetail/V2CreationSaleIpfs';
import useWalletAddress from '@/hooks/useWalletAddress';
import LikeActiveIcon from '@/icons/like-active-icon.svg';
import LikeInactiveIcon from '@/icons/like-inactive-icon.svg';
import PenIcon from '@/icons/pen-icon.svg';
import getCreationStatus from '@/utils/getCreationStatus';

import CreationAuction from './CreationAuction';
import styles from './CreationDetail.module.scss';

type Props = {
  creation: V2CreationData;
  token?: AniftyTokenDetailData | null;
  isLiked: boolean | undefined;
  isLikedLoading: boolean;
  onLikeButtonClick: () => void;
};

const CreationDetail: VFC<Props> = ({
  creation,
  token,
  isLiked,
  isLikedLoading,
  onLikeButtonClick,
}: Props) => {
  const { t, i18n } = useTranslation();

  const { currentAccountAddress } = useWalletAddress();

  const creationStatus = getCreationStatus(creation);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isNotReleasedYet =
    creation.reservedAt && isFuture(parseISO(creation.reservedAt));

  if (isNotReleasedYet && currentAccountAddress !== creation.creator.userID) {
    // Unreleased items are not visible to non-creator
    return <Error statusCode={404} />;
  }

  const isJA = i18n.language === 'ja';

  const isCreator = currentAccountAddress === creation.creator.userID;

  // Creator can sell or delete their creation
  // if it is neither listed nor minted
  const isListed = !!creation.mintAsk;
  const isMinted = !!creation.aniftyTokenID;
  const canSellOrDelete: boolean =
    isCreator && !isListed && !isMinted && !IS_LISTING_FEATURE_END;
  const isFinished = creation?.mintAsk
    ? creation?.mintAsk?.status === MintAskStatusType.Finished
    : false;
  const isSelling = creation?.mintAsk?.status === MintAskStatusType.OnGoing;
  const isBiddingStarted = creation?.mintBids
    ? creation?.mintBids.length > 0
    : false;
  return (
    <>
      <ImageExhibition
        imageURL={creation.imageURL}
        width={creation.width}
        height={creation.height}
      />

      {/* Edit details link */}
      <div className={styles.creationEditButtonSection}>
        {isCreator && !isBiddingStarted ? (
          <Link href={routes.editCreation(creation.creationID)}>
            <a className={styles.creationEditLink}>
              <PenIcon />
              {t('creationDetail.editDetails')}
            </a>
          </Link>
        ) : null}
        {isCreator || (
          <button
            type="button"
            className={styles.likeButton}
            onClick={() => onLikeButtonClick()}
          >
            <div className={styles.likeIconContainer}>
              {isLiked ? <LikeActiveIcon /> : <LikeInactiveIcon />}
              {isLikedLoading && (
                <div className={styles.likeIconSpinner}>
                  <Spinner size={2} />
                </div>
              )}
            </div>
            <p className={styles.likeCount}>{creation.likeSummary.likeCount}</p>
          </button>
        )}
        {creationStatus !== 'notListed' && (
          <div style={{ marginLeft: '16px' }}>
            <ShareSNSButton />
          </div>
        )}
      </div>

      <div className={styles.creationDetail}>
        {/* Left column */}
        <div>
          <CreationInfoSection
            creation={creation}
            creationStatus={creationStatus}
          />

          {creationStatus !== 'notListed' ? (
            <CreationOwnerList
              creationID={creation.creationID}
              aniftyTokenID={creation.aniftyTokenID}
            />
          ) : null}
        </div>

        {/* Right column */}
        {!IS_PURCHASE_FEATURE_END ? (
          <div>
            {(() => {
              if (creationStatus === 'notListed' && canSellOrDelete) {
                return (
                  <div className={styles.buttonContainer}>
                    {/* Sell Button */}
                    <Link
                      href={routes.sellCreation(creation.creationID.toString())}
                    >
                      <a className={styles.creationSellButton}>Sell</a>
                    </Link>

                    {/* Delete Button */}
                    <button
                      type="button"
                      className={styles.creationDeleteButton}
                      onClick={() => setIsDeleteModalOpen(true)}
                    >
                      Delete
                    </button>

                    <CreationDeleteModal
                      creationID={creation.creationID}
                      creationName={
                        (isJA && creation.titleJA) || creation.title
                      }
                      isOpen={isDeleteModalOpen}
                      onRequestClose={() => setIsDeleteModalOpen(false)}
                    />
                  </div>
                );
              }

              if (creationStatus === 'marketplaceV1' && token) {
                return <V1CreationSale token={token} />;
              }

              if (creationStatus === 'marketplaceV2') {
                return <V2CreationSale creation={creation} />;
              }

              if (creationStatus === 'marketplaceV2Ipfs') {
                return <V2CreationSaleIpfs creation={creation} />;
              }

              if (creationStatus === 'auctionIpfs') {
                return <CreationAuction creation={creation} />;
              }
            })()}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default CreationDetail;
